import { maxLength, notEmail } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import React, {
  type ChangeEvent,
  type FocusEvent,
  useState,
  useImperativeHandle,
  forwardRef,
  type Ref,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { getAboutBusinessClicked } from 'src/app/analytics/aboutBusiness';
import { TEALIUM_CAJA_ABOUT_BUSINESS_CHARACTER_COUNT } from 'src/app/constants/dispatcherId';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  ABOUT_THE_BUSINESS_INTERACTION,
  ABOUT_THE_BUSINESS_PRESSED,
} from 'src/app/dataAnalytics/schema/constants';
import { sendContentSafetyTrackingEvents } from 'src/app/dataAnalytics/utils/sendContentSafetyTrackingEvents';
import { sendPerUnitBillingValidationTrackingEvents } from 'src/app/dataAnalytics/utils/sendPerUnitBillingValidationTrackingEvents';
import {
  FEATURE_DATA_ANALYTICS,
  FEATURE_FILTER_CONTENT,
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { tealium as tealiumDispatcher } from 'src/app/state/dispatcher';
import { actionUpdateAboutBusiness } from 'src/app/state/modules/formData/aboutBusiness';
import { sendLinkEvent } from 'src/app/utils/analytics';
import {
  type Validators,
  filterContent,
  validateField,
} from 'src/app/utils/validation';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { Textarea } from 'src/app/views/shared/Textarea';
import type AppState from 'src/types/appState';
import type { AboutBusinessRef } from 'src/types/formFields';
import type { TrackingEvent } from 'src/types/trackingData';

import translations from './.vocab';

const INPUT_MAX_LENGTH = 1000;

export const AboutBusiness = forwardRef(
  (_props, ref: Ref<AboutBusinessRef>) => {
    const { aboutBusiness, tealium, jobLocationCountry, hirerNation } =
      useSelector(
        (state: AppState) => ({
          aboutBusiness: state.formData.aboutBusiness,
          tealium: state.tealium,
          jobLocationCountry: state.session?.jobLocationCountry,
          hirerNation: state.session?.hirerAccount?.nation,
        }),
        shallowEqual,
      );
    const { getLanguage } = useConfig();

    const country = (jobLocationCountry ?? hirerNation ?? 'AU').toLowerCase();
    const isFilterContentEnabled = useFeatureToggleData(FEATURE_FILTER_CONTENT);
    const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);
    const [validationMessage, setValidationMessage] = useState<string | null>(
      null,
    );
    const [inputValue, setInputValue] = useState<string>(aboutBusiness);

    const dispatch = useDispatch();

    const { t } = useTranslations(translations);
    const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

    const labelName = t('about-business-label');

    useImperativeHandle(ref, () => ({
      handleValidation: (skipIfNull?: boolean) =>
        handleInputValidation(inputValue, skipIfNull),
    }));

    const handleInputValidation = (value: string, skipIfNull = false) => {
      let validators: Validators = [maxLength];
      if (isFilterContentEnabled) validators = [...validators, filterContent];
      if (isPerUnitBilling) validators = [...validators, notEmail];

      const validationResult = validateField(
        {
          field: 'aboutBusiness',
          validators,
          validatorProps: {
            max: INPUT_MAX_LENGTH,
            country,
            sendTrackingEvents: sendContentSafetyTrackingEvents,
          },
          value,
        },
        skipIfNull,
        getLanguage(),
        [sendPerUnitBillingValidationTrackingEvents],
      );

      setValidationMessage(validationResult);

      return validationResult;
    };

    const handleOnBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
      const { value } = event.target;
      const characterCount = value?.length || 0;
      const errorMessage = handleInputValidation(value);
      if (!errorMessage) {
        dispatch(
          tealiumDispatcher(TEALIUM_CAJA_ABOUT_BUSINESS_CHARACTER_COUNT, value),
        );
        dispatch(actionUpdateAboutBusiness(value));
      }

      if (isPageAnalyticsEnabled) {
        const eventData: TrackingEvent = {
          eventName: ABOUT_THE_BUSINESS_PRESSED,
          objectInteraction: ABOUT_THE_BUSINESS_INTERACTION,
          cajaAboutBusinessCharacterCount: characterCount,
        };
        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        sendLinkEvent(tealium, getAboutBusinessClicked(characterCount));
      }
    };

    const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = event.target;
      setInputValue(value);
      handleInputValidation(value);
    };

    return (
      <Textarea
        hasCounter
        id="AboutBusinessTextArea"
        label={labelName}
        secondaryLabel={t('about-business-secondary-label')}
        description={t('about-business-description')}
        maxLength={INPUT_MAX_LENGTH}
        message={validationMessage}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        valid={!validationMessage}
        value={inputValue}
      />
    );
  },
);
